// Bare module accessor — used by api.ts to avoid React hook import cycles.

export type DevSettings = {
  apiBase: string;
  mock: boolean;
  fullLayer: boolean;
};

export const DEFAULT_DEV_SETTINGS: DevSettings = {
  apiBase: '',
  mock: false,
  fullLayer: false,
};

const STORAGE_KEY = 'ienone:devSettings';
const DEV_MODE_KEY = 'ienone:dev';

// 開発ツールは Vite の dev サーバでだけ有効にする。本番は他サービスと同じオリジンで配信するので、
// localStorage は自分だけのものではない。そこに置かれた値で API の向き先やモック表示が
// 切り替わると、よそのページの不具合ひとつで恒久的に書き換えられてしまう。
const DEV_TOOLS = import.meta.env.DEV;

export function readDevSettings(): DevSettings {
  if (!DEV_TOOLS) return DEFAULT_DEV_SETTINGS;
  if (typeof window === 'undefined') return DEFAULT_DEV_SETTINGS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_DEV_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<DevSettings>;
    return { ...DEFAULT_DEV_SETTINGS, ...parsed };
  } catch {
    return DEFAULT_DEV_SETTINGS;
  }
}

export function writeDevSettings(settings: DevSettings): void {
  if (!DEV_TOOLS) return;
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  window.dispatchEvent(new Event('ienone:dev-settings-changed'));
}

export function isDevMode(): boolean {
  if (!DEV_TOOLS) return false;
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(DEV_MODE_KEY) === '1';
}

export function setDevMode(enabled: boolean): void {
  if (!DEV_TOOLS) return;
  if (typeof window === 'undefined') return;
  if (enabled) window.localStorage.setItem(DEV_MODE_KEY, '1');
  else window.localStorage.removeItem(DEV_MODE_KEY);
  window.dispatchEvent(new Event('ienone:dev-mode-changed'));
}

export const DEV_SETTINGS_STORAGE_KEY = STORAGE_KEY;
export const DEV_MODE_STORAGE_KEY = DEV_MODE_KEY;
